import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LoginPageComponent } from './viewbid/shared/pages/login-page/login-page.component';
import { TermsAndConditionsPageComponent } from './viewbid/shared/pages/terms-and-conditions-page/terms-and-conditions-page.component';
import { PrivacyPolicyPageComponent } from './viewbid/shared/pages/privacy-policy-page/privacy-policy-page.component';
import { VerifyEmailPageComponent } from './viewbid/shared/pages/verify-email-page/verify-email-page.component';
import { ProfilesModule } from './viewbid/modules/profiles/profiles.module';
import { PostLoginPageComponent } from './viewbid/shared/pages/post-login-page/post-login-page.component';
import { AuctionsModule } from './viewbid/modules/auctions/auctions.module';
import { HomeModule } from './viewbid/modules/home/home.module';
import { NotFoundPageComponent } from './viewbid/shared/pages/not-found-page/not-found-page.component';
import { AboutPageComponent } from './viewbid/shared/pages/about-page/about-page.component';
import { FaqPageComponent } from './viewbid/shared/pages/faq-page/faq-page.component';
import { LayoutComponent } from './viewbid/shared/components/layout/layout.component';
import { ContactUsPageComponent } from './viewbid/shared/pages/contact-us-page/contact-us-page.component';
import { UnsubscriptionPageComponent } from './viewbid/shared/pages/unsubscription-page/unsubscription-page.component';
import { SiteMapPageComponent } from './viewbid/shared/pages/site-map-page/site-map-page.component';
import { InvoiceActionsPageComponent } from './viewbid/modules/profiles/pages/invoice-actions-page/invoice-actions-page.component';
import { AuthGuard } from './auth.guard';
import { AuthGuard as ProfileAuthGuard } from './viewbid/modules/profiles/auth.guard';
import { PendingPickupGuard } from './pending-pickup.guard';
import { NewsSectionPageComponent } from './viewbid/shared/pages/news-section-page/news-section-page.component';
import { NewsArticlePageComponent } from './viewbid/shared/pages/news-article-page/news-article-page.component';
import { uuidParamsMatchGuard } from './viewbid/shared/guards/uuid-params-match.guard';

const routes: Routes = [
    {
        path: '',
        component: LayoutComponent, // Use the LayoutComponent as the wrapper
        children: [
            {
                path: '',
                redirectTo: 'home',
                pathMatch: 'full',
            },
            {
                path: 'home',
                loadChildren: () => HomeModule,
                canActivate: [AuthGuard, PendingPickupGuard],
                data: { title: 'Viewbid Auctions' },
            },
            {
                path: 'profiles',
                loadChildren: () => ProfilesModule,
                canActivate: [PendingPickupGuard],
                data: { title: 'Viewbid Auctions - Profiles' },
            },
            {
                path: 'auctions',
                loadChildren: () => AuctionsModule,
                data: { title: 'Viewbid Auctions - Auctions' },
                canActivate: [AuthGuard, PendingPickupGuard],
            },
            {
                path: 'about-us',
                component: AboutPageComponent,
                data: { title: 'Viewbid Auctions - About Us' },
                canActivate: [AuthGuard, PendingPickupGuard],
            },
            {
                path: 'contact-us',
                component: ContactUsPageComponent,
                data: { title: 'Viewbid Auctions - Contact Us' },
                canActivate: [AuthGuard, PendingPickupGuard],
            },
            {
                path: 'faq',
                component: FaqPageComponent,
                data: { title: 'Viewbid Auctions - FAQ' },
                canActivate: [AuthGuard, PendingPickupGuard],
            },
            {
                path: 'news',
                component: NewsSectionPageComponent,
                data: { title: 'Viewbid Auctions - News' },
                canActivate: [AuthGuard, PendingPickupGuard],
            },
            {
                path: 'news/:articleId',
                component: NewsArticlePageComponent,
                data: { title: 'Viewbid Auctions - News' },
                canActivate: [AuthGuard, PendingPickupGuard],
            },
            {
                path: 'site-map',
                component: SiteMapPageComponent,
                data: { title: 'Viewbid Auctions -Contact Us' },
                canActivate: [AuthGuard, PendingPickupGuard],
            },
            {
                path: 'invoice-actions',
                component: InvoiceActionsPageComponent,
                canActivate: [ProfileAuthGuard, AuthGuard],
            },
            {
                path: 'newsletters/unsubscribe/:subscriptionId',
                component: UnsubscriptionPageComponent,
                data: { title: 'Viewbid Auction -Unsubscribe' },
                canActivate: [AuthGuard],
                canMatch: [uuidParamsMatchGuard],
            },
            {
                path: 'terms-and-conditions',
                component: TermsAndConditionsPageComponent,
                data: { title: 'Viewbid Auctions - Terms And Conditions' },
                canActivate: [AuthGuard],
            },
            {
                path: 'privacy-policy',
                component: PrivacyPolicyPageComponent,
                data: { title: 'Viewbid Auctions - Privacy Policy' },
                canActivate: [AuthGuard],
            },
            {
                path: 'verify-email',
                component: VerifyEmailPageComponent,
                data: { title: 'Viewbid Auctions - Verify Email' },
                canActivate: [AuthGuard],
            },
        ],
    },
    // Bare — deliberately outside LayoutComponent. Both are pure
    // redirect-in-progress trampolines (loginWithRedirect() to Auth0, or a
    // spinner while post-login sorts out where to send the reader); mounting
    // them under the layout would flash the header/footer/nav chrome around
    // them before the browser actually leaves for Auth0 or the real
    // destination resolves.
    {
        path: 'login',
        component: LoginPageComponent,
        data: { title: 'Viewbid Auctions - Login' },
    },
    {
        path: 'post-login',
        component: PostLoginPageComponent,
    },
    {
        path: '404',
        component: NotFoundPageComponent,
    },
    {
        path: '**',
        component: NotFoundPageComponent,
    },
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, {
            useHash: false,
            anchorScrolling: 'enabled',
            // 'disabled' on purpose — NOT the stock 'enabled'. 'enabled'
            // scrolls to the top on EVERY imperative navigation, including
            // ones that only rewrite query params (the list pages sync
            // page/sortBy into the URL mid-scroll, and the viewport would
            // visibly jump). AppComponent.handleRouterScrolling() reinstates
            // the two behaviors worth keeping: back/forward restores the
            // remembered offset, and a path change starts the new page at the
            // top. Keep anchorScrolling: it also keeps the RouterScroller
            // alive, which emits the Scroll events that handler consumes.
            scrollPositionRestoration: 'disabled',
        }),
    ],
    exports: [RouterModule],
})
export class AppRoutingModule {}
